// V2 material-status chip — "Materials Ready" / "N Materials Missing" based
// on a viewport-triggered availability check.
//
// Two deliberate improvements over the v1 chip it ports:
//  1. The check runs for the step's REMAINING quantity (v1 asked for 1 unit,
//     so a WO for 100 showed "Ready" with material for one).
//  2. Clicking a shortage chip opens the structured shortage dialog
//     (Material / Required / Available / Shortage) instead of window.alert.

import React, { useEffect, useRef, useState } from 'react';
import { Chip } from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import { v2 } from '../../../../services/api';
import MaterialShortageDialogV2 from './MaterialShortageDialogV2';

export default function MaterialStatusChipV2({ step }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const ref = useRef(null);

  const total = parseFloat(step?.quantity_to_produce) || 0;
  const done = parseFloat(step?.quantity_completed) || 0;
  const remaining = Math.max(0, total - done);
  // Fully-complete steps still get a 1-unit sanity check (matches the start
  // button's `remaining || 1` contract).
  const checkQuantity = remaining > 0 ? remaining : 1;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !step?.work_order_id) return undefined;
    let cancelled = false;
    async function check() {
      setLoading(true);
      try {
        const res = await v2.workOrders.materialAvailability(
          step.work_order_id,
          { quantity: checkQuantity, step: step.step_id },
        );
        if (!cancelled) setStatus(res.data);
      } catch (err) {
        console.error('MaterialStatusChipV2: failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [isVisible, step?.work_order_id, step?.step_id, checkQuantity]);

  if (!isVisible || loading) {
    return (
      <span ref={ref}>
        <Chip label="Checking..." size="small" color="default" />
      </span>
    );
  }
  if (!status) {
    return (
      <span ref={ref}>
        <Chip label="Unknown" size="small" color="default" />
      </span>
    );
  }

  if (status.shortages && status.shortages.length > 0) {
    return (
      <span ref={ref}>
        <Chip
          data-testid={`material-status-chip-${step.step_id}`}
          label={`${status.shortages.length} Material${status.shortages.length > 1 ? 's' : ''} Missing`}
          size="small"
          color="error"
          icon={<Warning />}
          onClick={(e) => {
            e.stopPropagation();
            setDialogOpen(true);
          }}
          sx={{ cursor: 'pointer' }}
        />
        {dialogOpen && (
          <MaterialShortageDialogV2
            open
            onClose={() => setDialogOpen(false)}
            woNumber={step.wo_number}
            shortages={status.shortages}
            title="Material Shortage"
            intro={
              `Not enough material to make the remaining ${checkQuantity} `
              + `unit${checkQuantity === 1 ? '' : 's'}. This is what's holding it back:`
            }
          />
        )}
      </span>
    );
  }
  return (
    <span ref={ref}>
      <Chip
        data-testid={`material-status-chip-${step.step_id}`}
        label="Materials Ready"
        size="small"
        color="success"
        icon={<CheckCircle />}
      />
    </span>
  );
}
